// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { ApiUser } from '../../types/accounts';
import { consoleService } from '../../services/consoleService';
import { Notification } from '../../types/notifications';

interface NotificationsTabProps {
  account: ApiUser;
}

const NotificationsTab: React.FC<NotificationsTabProps> = ({ account }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);

  useEffect(() => {
    loadNotifications();
  }, [account.id]);

  const loadNotifications = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await consoleService.listNotifications('', { user_id: account.id });
      setNotifications(response.notifications || []);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (notificationId: string) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) {
      return;
    }

    setDeleting(notificationId);
    try {
      await consoleService.deleteNotification('', {
        notification_id: notificationId,
        user_id: account.id
      });
      setNotifications(notifications.filter(n => n.id !== notificationId));
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete notification');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  const getContentPreview = (content: string) => {
    try {
      const parsed = JSON.parse(content);
      if (parsed.title) return parsed.title;
      if (parsed.message) return parsed.message.substring(0, 50) + (parsed.message.length > 50 ? '...' : '');
      return content.substring(0, 50) + (content.length > 50 ? '...' : '');
    } catch {
      return content.substring(0, 50) + (content.length > 50 ? '...' : '');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p style={{ marginTop: '12px', color: '#6c757d' }}>Loading notifications...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {error && (
        <div className="alert alert-danger" style={{ marginBottom: '20px' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {notifications.length === 0 && !error ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6c757d' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔔</div>
          <h5 style={{ marginBottom: '8px', color: '#495057' }}>No notifications found</h5>
          <p style={{ margin: 0 }}>This user has no notifications.</p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th>ID</th>
                <th>User ID</th>
                <th>Sender ID</th>
                <th>Subject</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {notifications.map((notification) => (
                <tr 
                  key={notification.id} 
                  onClick={() => setSelectedNotification(notification)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <code style={{ fontSize: '11px', background: '#f1f3f4', padding: '2px 6px', borderRadius: '4px' }}>
                      {notification.id.substring(0, 8)}...
                    </code>
                  </td>
                  <td>
                    <code style={{ fontSize: '11px', background: '#f1f3f4', padding: '2px 6px', borderRadius: '4px' }}>
                      {notification.user_id || account.id}
                    </code>
                  </td>
                  <td>
                    {notification.sender_id ? (
                      <code style={{ fontSize: '11px', background: '#f1f3f4', padding: '2px 6px', borderRadius: '4px' }}>
                        {notification.sender_id}
                      </code>
                    ) : (
                      <span style={{ color: '#adb5bd' }}>-</span>
                    )}
                  </td>
                  <td style={{ fontWeight: 500 }}>{notification.subject || '-'}</td>
                  <td style={{ color: '#6c757d', fontSize: '13px' }}>{formatDate(notification.create_time)}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(notification.id);
                      }}
                      disabled={deleting === notification.id}
                      title="Delete notification"
                    >
                      {deleting === notification.id ? '...' : '🗑️'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Notification Content Modal */}
      {selectedNotification && (
        <div 
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1050
          }} 
          onClick={() => setSelectedNotification(null)}
        >
          <div 
            style={{
              background: '#fff',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '80vh',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '24px' }}>🔔</span>
                <h5 style={{ margin: 0, color: '#fff', fontWeight: 600 }}>
                  {selectedNotification.subject || 'Notification'}
                </h5>
              </div>
              <button
                onClick={() => setSelectedNotification(null)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  color: '#fff',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px' }}>
              <div style={{ 
                display: 'flex', 
                gap: '16px', 
                marginBottom: '20px',
                fontSize: '13px',
                color: '#6c757d'
              }}>
                <span>📅 {formatDate(selectedNotification.create_time)}</span>
              </div>

              <div style={{ marginBottom: '8px', fontWeight: 600, color: '#495057' }}>
                Content
              </div>
              <pre style={{ 
                backgroundColor: '#f8f9fa', 
                padding: '16px', 
                borderRadius: '8px',
                margin: 0,
                maxHeight: '300px',
                overflow: 'auto',
                fontSize: '13px',
                lineHeight: '1.5',
                border: '1px solid #e9ecef'
              }}>
                {typeof selectedNotification.content === 'string' 
                  ? (() => {
                      try {
                        return JSON.stringify(JSON.parse(selectedNotification.content), null, 2);
                      } catch {
                        return selectedNotification.content;
                      }
                    })()
                  : JSON.stringify(selectedNotification.content, null, 2)
                }
              </pre>
            </div>

            {/* Footer */}
            <div style={{ 
              padding: '16px 24px', 
              borderTop: '1px solid #e9ecef',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setSelectedNotification(null)}
                style={{
                  background: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 24px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsTab;
